import React, { useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Dropdown, Input, Modal, Row, Select, Space, Statistic, Table, Tag, Upload } from 'antd';
import { PlusOutlined, SearchOutlined, DownloadOutlined, UploadOutlined, MoreOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTradingRentalEstimations, TR_ESTIMATION_KEYS, useDeleteTradingRentalEstimation } from '@/hooks/useTradingRentalEstimation';
import { useOpportunities } from '@/hooks/useOpportunities';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useExcelMigrationIo } from '@/hooks/useExcelMigrationIo';
import { useAuthStore } from '@/store/authStore';
import type { TradingRentalEstimation } from '@/services/tradingRentalEstimation.service';

const { RangePicker } = DatePicker;

import { canDeleteTradingRentalEstimation } from '../../utils/documentDeletePolicy';

function canDeleteEstimation(record: Pick<TradingRentalEstimation, 'status' | 'related_quotation_count' | 'approval_status'>): boolean {
  return canDeleteTradingRentalEstimation(record).ok;
}

const TradingRentalEstimationsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { formatCurrency } = useCurrency();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [type, setType] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [teamFilter, setTeamFilter] = useState<string | undefined>(
    () => searchParams.get('sales_team_id') || undefined
  );
  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });
  const filters = useMemo(
    () => ({
      search: search || undefined,
      status,
      estimation_type: type,
      fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
      toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
      sales_team_id: teamFilter || undefined,
    }),
    [search, status, type, dateRange, teamFilter]
  );
  const { data = [], isLoading } = useTradingRentalEstimations(filters);
  const deleteMutation = useDeleteTradingRentalEstimation();
  const { user } = useAuthStore();
  const canDelete =
    Boolean(user?.is_super_admin) ||
    (user?.permissions || []).includes('growth.trading_rental_estimation.delete') ||
    (user?.permissions || []).includes('growth.view');

  const handleDelete = (record: TradingRentalEstimation) => {
    Modal.confirm({
      title: 'Delete estimation?',
      content: `Delete Trading/Rental estimation "${record.estimation_number}"? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => deleteMutation.mutateAsync(record.id),
    });
  };
  const { isExcelBusy, downloadTemplate, downloadExport, handleImport } = useExcelMigrationIo({
    templateUrl: '/sales/trading-rental-estimations/excel/template',
    exportUrl: '/sales/trading-rental-estimations/excel/export',
    importUrl: '/sales/trading-rental-estimations/excel/import',
    templateFilename: 'trading-rental-estimations-import-template.xlsx',
    exportFilenamePrefix: 'trading-rental-estimations',
    onImportSuccess: () => queryClient.invalidateQueries({ queryKey: TR_ESTIMATION_KEYS.lists() }),
    buildExportParams: () => ({
      search: filters.search,
      status: filters.status,
      estimation_type: filters.estimation_type,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      sales_team_id: filters.sales_team_id,
    }),
  });
  const { data: opportunities = [] } = useOpportunities();
  const opportunityById = useMemo(
    () =>
      new Map(
        opportunities.map((o: any) => [
          o.id,
          `${o.opportunity_number || ''}${o.opportunity_name ? ` — ${o.opportunity_name}` : ''}`.trim(),
        ])
      ),
    [opportunities]
  );

  const totalValue = data.reduce((s, row) => s + Number(row.grand_total || 0), 0);
  const draftCount = data.filter((x) => x.status === 'Draft').length;
  const convertedCount = data.filter((x) => String(x.status).toLowerCase().includes('converted')).length;

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <h2 style={{ margin: 0 }}>Trading & Rental Estimations</h2>
            </Col>
            <Col>
              <Space>
                <Button icon={<DownloadOutlined />} onClick={downloadTemplate} disabled={isExcelBusy}>
                  Template
                </Button>
                <Button icon={<DownloadOutlined />} onClick={downloadExport} disabled={isExcelBusy}>
                  Export
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  customRequest={handleImport}
                  disabled={isExcelBusy}
                >
                  <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                    Import
                  </Button>
                </Upload>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/growth/estimations/trading-rental/create')}>
                  New Estimation
                </Button>
              </Space>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Statistic title="Total estimations" value={data.length} />
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Draft estimations" value={draftCount} />
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Converted estimations" value={convertedCount} />
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Total value" value={totalValue} formatter={(v) => formatCurrency(Number(v || 0))} />
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined />}
                placeholder="Search by number, customer, project"
                allowClear
              />
            </Col>
            <Col xs={24} md={5}>
              <Select
                style={{ width: '100%' }}
                placeholder="Status"
                allowClear
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Submitted', label: 'Submitted' },
                  { value: 'Approved', label: 'Approved' },
                  { value: 'Converted to Quotation', label: 'Converted to Quotation' },
                  { value: 'Partially Converted', label: 'Partially Converted' },
                  { value: 'Cancelled', label: 'Cancelled' },
                ]}
              />
            </Col>
            <Col xs={24} md={5}>
              <Select
                style={{ width: '100%' }}
                placeholder="Type"
                allowClear
                value={type}
                onChange={setType}
                options={[
                  { value: 'Industrial Trading', label: 'Industrial Trading' },
                  { value: 'Rental Services', label: 'Rental Services' },
                  { value: 'Mixed', label: 'Mixed' },
                ]}
              />
            </Col>
            <Col xs={24} md={5}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Sales team"
                value={teamFilter}
                onChange={setTeamFilter}
                options={salesTeams.map((t) => ({ value: t.id, label: t.team_name }))}
              />
            </Col>
            <Col xs={24} md={6}>
              <RangePicker style={{ width: '100%' }} value={dateRange} onChange={setDateRange} />
            </Col>
          </Row>

          <Table
            rowKey="id"
            loading={isLoading}
            dataSource={data}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            columns={[
              {
                title: 'Estimation #',
                dataIndex: 'estimation_number',
                render: (v: string, record: any) => (
                  <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/estimations/trading-rental/${record.id}`)}>
                    {v}
                  </Button>
                ),
              },
              { title: 'Type', dataIndex: 'estimation_type' },
              { title: 'Customer', dataIndex: 'customer_name' },
              { title: 'Sales team', dataIndex: 'sales_team_name', render: (v: string) => v || '—' },
              { title: 'Project/Site', dataIndex: 'project_name', render: (v: string) => v || '—' },
              {
                title: 'Opportunity',
                dataIndex: 'opportunity_id',
                render: (v: string, record: any) =>
                  record?.opportunity_number ||
                  (v ? opportunityById.get(v) || '—' : '—'),
              },
              {
                title: 'Value',
                dataIndex: 'grand_total',
                align: 'right' as const,
                render: (v: number) => formatCurrency(Number(v || 0)),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                render: (v: string) => {
                  const color =
                    v === 'Approved'
                      ? 'green'
                      : v === 'Cancelled'
                        ? 'red'
                        : v.toLowerCase().includes('converted')
                          ? 'blue'
                          : 'gold';
                  return <Tag color={color}>{v}</Tag>;
                },
              },
              {
                title: 'Date',
                dataIndex: 'estimation_date',
                render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
              },
              {
                title: 'Related quotations',
                dataIndex: 'related_quotation_count',
                align: 'center' as const,
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 80,
                render: (_: unknown, record: TradingRentalEstimation) => {
                  const menuItems: any[] = [
                    {
                      key: 'view',
                      icon: <EyeOutlined />,
                      label: 'View',
                      onClick: () => navigate(`/growth/estimations/trading-rental/${record.id}`),
                    },
                    {
                      key: 'edit',
                      icon: <EditOutlined />,
                      label: 'Edit',
                      onClick: () => navigate(`/growth/estimations/trading-rental/${record.id}`),
                    },
                  ];
                  if (canDelete) {
                    menuItems.push(
                      { type: 'divider' as const },
                      {
                        key: 'delete',
                        icon: <DeleteOutlined />,
                        label: 'Delete',
                        danger: true,
                        disabled: !canDeleteEstimation(record),
                        onClick: () => handleDelete(record),
                      }
                    );
                  }
                  return (
                    <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                      <Button type="text" icon={<MoreOutlined />} aria-label="Actions" />
                    </Dropdown>
                  );
                },
              },
            ]}
          />
        </Space>
      </Card>
    </div>
  );
};

export default TradingRentalEstimationsPage;

